import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  ViewEncapsulation,
} from "@angular/core";
import {
  createChart,
  CrosshairMode,
  LineStyle,
  IChartApi,
  ISeriesApi,
} from "lightweight-charts";

interface TradeMarker {
  time: any;
  position: "aboveBar" | "belowBar";
  color: string;
  shape: "circle" | "square" | "arrowUp" | "arrowDown";
  text: string;
}

interface DrawingTool {
  id: string;
  name: string;
  icon: string;
  active: boolean;
}

interface Drawing {
  id: string;
  type: string;
  points: Array<{ time: number; price: number }>;
  color: string;
}

@Component({
  selector: "app-insa-trader",
  templateUrl: "./insa-trader.component.html",
  styles: [require("./insa-trader.component.css").toString()],
  encapsulation: ViewEncapsulation.None,
})
export class InsaTraderComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("chartContainer", { static: false }) chartContainer: ElementRef;
  @ViewChild("drawingCanvas", { static: false }) drawingCanvas: ElementRef;

  private chart: IChartApi;
  private candlestickSeries: ISeriesApi<"Candlestick">;
  private volumeSeries: any;
  private ema20Series: any;
  private ema50Series: any;
  private ema200Series: any;
  private bbUpperSeries: any;
  private bbLowerSeries: any;
  private resizeObserver: any;

  private ctx: CanvasRenderingContext2D;
  private isDrawing = false;
  private drawingStartPoint: {
    x: number;
    y: number;
    time: number;
    price: number;
  } | null = null;
  private drawingColor = "#2962FF";

  drawingTools: DrawingTool[] = [
    { id: "trendline", name: "Trend Line", icon: "📈", active: false },
    { id: "horizontal", name: "Horizontal Line", icon: "━", active: false },
    { id: "vertical", name: "Vertical Line", icon: "┃", active: false },
    { id: "rectangle", name: "Rectangle", icon: "▭", active: false },
    { id: "fibonacci", name: "Fibonacci", icon: "Φ", active: false },
    { id: "arrow", name: "Arrow", icon: "→", active: false },
  ];

  selectedDrawingTool: string | null = null;
  drawings: Drawing[] = [];
  selectedSymbol = "TSLA";
  selectedAssetName = "Tesla Inc.";
  currentPrice = 315.98;
  priceChange = -4.42;
  priceChangePercent = -1.4;
  volume24h = 22704054;
  high24h = 326.26;
  low24h = 293.44;

  timeframes = ["1m", "5m", "15m", "1H", "4H", "1D", "1W"];

  indicators = [
    { name: "EMA 20", enabled: true, color: "#2962FF" },
    { name: "EMA 50", enabled: true, color: "#FF6D00" },
    { name: "EMA 200", enabled: true, color: "#E91E63" },
    { name: "Bollinger Bands", enabled: true, color: "#9C27B0" },
    { name: "Volume", enabled: true, color: "#26A69A" },
  ];

  favorites = [
    { symbol: "TSLA", name: "Tesla Inc.", price: 245.62, change: 2.27 },
    { symbol: "AAPL", name: "Apple Inc.", price: 177.1, change: 1.27 },
    { symbol: "MSFT", name: "Microsoft Corp.", price: 416.29, change: -0.79 },
  ];

  allAssets = [
    { symbol: "TSLA", name: "Tesla Inc.", price: 245.62, change: 2.27 },
    { symbol: "AAPL", name: "Apple Inc.", price: 177.1, change: 1.27 },
    { symbol: "MSFT", name: "Microsoft Corp.", price: 416.29, change: -0.79 },
    { symbol: "GOOGL", name: "Alphabet Inc.", price: 142.56, change: 0.95 },
    { symbol: "AMZN", name: "Amazon.com Inc.", price: 175.89, change: 1.23 },
    { symbol: "NVDA", name: "NVIDIA Corp.", price: 875.45, change: 3.67 },
    { symbol: "META", name: "Meta Platforms", price: 485.21, change: -1.12 },
    {
      symbol: "BRK.B",
      name: "Berkshire Hathaway",
      price: 398.76,
      change: 0.45,
    },
    { symbol: "JPM", name: "JPMorgan Chase", price: 182.34, change: -0.67 },
    { symbol: "V", name: "Visa Inc.", price: 267.89, change: 1.89 },
  ];

  filteredAssets = [...this.allAssets];
  searchQuery = "";
  showSearchResults = false;
  searchFocused = false;

  selectedOrderType = "Limit";
  tradePrice = 245.67;
  tradeAmount = 0;
  tradeTotal = 0;
  sliderPercent = 0;

  recentTrades: any[] = [];

  private currentCandleData: any[] = [];
  private currentVolumeData: any[] = [];
  private currentEma20Data: any[] = [];
  private currentEma50Data: any[] = [];
  private currentEma200Data: any[] = [];
  private currentBBData: any = { upper: [], lower: [] };

  ngOnInit(): void {
    this.generateRecentTrades();
    this.tradePrice = this.currentPrice;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initChart();
      this.initDrawingCanvas();
      this.loadChartData();
      this.setupRealtimeUpdates();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.chart) {
      this.chart.remove();
    }
  }

  private initChart(): void {
    const container = this.chartContainer.nativeElement;

    this.chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight || 500,
      layout: {
        backgroundColor: "#ffffff",
        textColor: "#131722",
      },
      grid: {
        vertLines: { color: "rgba(42, 46, 57, 0.1)" },
        horzLines: { color: "rgba(42, 46, 57, 0.1)" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          width: 1,
          color: "rgba(42, 46, 57, 0.4)",
          style: LineStyle.Dashed,
        },
        horzLine: {
          width: 1,
          color: "rgba(42, 46, 57, 0.4)",
          style: LineStyle.Dashed,
        },
      },
      rightPriceScale: {
        borderColor: "rgba(42, 46, 57, 0.3)",
        scaleMargins: { top: 0.1, bottom: 0.2 },
      },
      timeScale: {
        borderColor: "rgba(42, 46, 57, 0.3)",
        timeVisible: true,
        secondsVisible: false,
      },
    });

    this.candlestickSeries = this.chart.addCandlestickSeries({
      upColor: "#1A8700",
      downColor: "#D13A3A",
      borderVisible: false,
      wickUpColor: "#1A8700",
      wickDownColor: "#D13A3A",
    });

    this.volumeSeries = this.chart.addHistogramSeries({
      color: "#1A8700",
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
    });

    this.chart.priceScale("volume").applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    this.ema20Series = this.chart.addLineSeries({
      color: "#2962FF",
      lineWidth: 2,
      title: "EMA 20",
    });
    this.ema50Series = this.chart.addLineSeries({
      color: "#FF6D00",
      lineWidth: 2,
      title: "EMA 50",
    });
    this.ema200Series = this.chart.addLineSeries({
      color: "#E91E63",
      lineWidth: 2,
      title: "EMA 200",
    });

    this.bbUpperSeries = this.chart.addLineSeries({
      color: "rgba(156, 39, 176, 0.5)",
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      title: "BB Upper",
    });

    this.bbLowerSeries = this.chart.addLineSeries({
      color: "rgba(156, 39, 176, 0.5)",
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      title: "BB Lower",
    });

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver((entries) => {
        if (entries.length === 0 || entries[0].target !== container) return;
        const newRect = entries[0].contentRect;
        this.chart.applyOptions({
          width: newRect.width,
          height: newRect.height,
        });
      });
      this.resizeObserver.observe(container);
    }
  }

  private loadChartData(): void {
    if (!this.candlestickSeries) return;

    this.currentCandleData = this.generateCandlestickData(500);
    this.currentVolumeData = this.generateVolumeData(this.currentCandleData);
    this.currentEma20Data = this.calculateEMA(this.currentCandleData, 20);
    this.currentEma50Data = this.calculateEMA(this.currentCandleData, 50);
    this.currentEma200Data = this.calculateEMA(this.currentCandleData, 200);
    this.currentBBData = this.calculateBollingerBands(
      this.currentCandleData,
      20,
      2,
    );

    this.candlestickSeries.setData(this.currentCandleData);
    this.volumeSeries.setData(this.currentVolumeData);
    if (this.ema20Series) this.ema20Series.setData(this.currentEma20Data);
    if (this.ema50Series) this.ema50Series.setData(this.currentEma50Data);
    if (this.ema200Series) this.ema200Series.setData(this.currentEma200Data);
    if (this.bbUpperSeries)
      this.bbUpperSeries.setData(this.currentBBData.upper);
    if (this.bbLowerSeries)
      this.bbLowerSeries.setData(this.currentBBData.lower);

    const markers = this.generateTradeMarkers(this.currentCandleData);
    this.candlestickSeries.setMarkers(markers);

    const lastCandle =
      this.currentCandleData[this.currentCandleData.length - 1];
    const prevCandle =
      this.currentCandleData[this.currentCandleData.length - 2];
    this.currentPrice = lastCandle.close;
    this.priceChange = lastCandle.close - prevCandle.close;
    this.priceChangePercent = (this.priceChange / prevCandle.close) * 100;

    this.high24h = Math.max(
      ...this.currentCandleData.slice(-24).map((c) => c.high),
    );
    this.low24h = Math.min(
      ...this.currentCandleData.slice(-24).map((c) => c.low),
    );
    this.volume24h = this.currentVolumeData
      .slice(-24)
      .reduce((sum, v) => sum + v.value, 0);
  }

  private generateCandlestickData(count: number): any[] {
    const data: any[] = [];
    let basePrice = this.currentPrice;
    const now = Math.floor(Date.now() / 1000);
    const interval = 3600;

    for (let i = 0; i < count; i++) {
      const time = now - (count - i) * interval;
      const volatility = basePrice * 0.02;
      const open = basePrice + (Math.random() - 0.5) * volatility;
      const close = open + (Math.random() - 0.5) * volatility * 1.5;
      const high = Math.max(open, close) + Math.random() * volatility * 0.5;
      const low = Math.min(open, close) - Math.random() * volatility * 0.5;

      data.push({ time, open, high, low, close });
      basePrice = close + (Math.random() - 0.48) * (basePrice * 0.01);
    }

    return data;
  }

  private generateVolumeData(candleData: any[]): any[] {
    return candleData.map((candle) => ({
      time: candle.time,
      value: Math.random() * 1000000 + 500000,
      color:
        candle.close >= candle.open
          ? "rgba(26, 135, 0, 0.5)"
          : "rgba(209, 58, 58, 0.5)",
    }));
  }

  private calculateEMA(data: any[], period: number): any[] {
    const ema: any[] = [];
    const multiplier = 2 / (period + 1);
    let emaValue = data[0].close;

    for (let i = 0; i < data.length; i++) {
      emaValue = (data[i].close - emaValue) * multiplier + emaValue;
      ema.push({ time: data[i].time, value: emaValue });
    }

    return ema;
  }

  private calculateBollingerBands(data: any[], period: number, stdDev: number) {
    const upper: any[] = [];
    const lower: any[] = [];

    for (let i = period - 1; i < data.length; i++) {
      const slice = data.slice(i - period + 1, i + 1);
      const avg = slice.reduce((sum, c) => sum + c.close, 0) / period;
      const variance =
        slice.reduce((sum, c) => sum + Math.pow(c.close - avg, 2), 0) / period;
      const std = Math.sqrt(variance);

      upper.push({ time: data[i].time, value: avg + stdDev * std });
      lower.push({ time: data[i].time, value: avg - stdDev * std });
    }

    return { upper, lower };
  }

  private generateTradeMarkers(data: any[]): TradeMarker[] {
    const markers: TradeMarker[] = [];

    for (
      let i = 50;
      i < data.length;
      i += Math.floor(Math.random() * 40 + 30)
    ) {
      const isBuy = Math.random() > 0.5;
      markers.push({
        time: data[i].time,
        position: isBuy ? "belowBar" : "aboveBar",
        color: isBuy ? "#26a69a" : "#ef5350",
        shape: isBuy ? "arrowUp" : "arrowDown",
        text: isBuy ? "B" : "S",
      });
    }

    return markers;
  }

  private generateRecentTrades(): void {
    const basePrice = this.currentPrice;
    const now = Date.now();

    this.recentTrades = Array(20)
      .fill(null)
      .map((_, i) => ({
        price: basePrice + (Math.random() - 0.5) * (basePrice * 0.01),
        amount: parseFloat((Math.random() * 150 + 5).toFixed(2)),
        time: new Date(now - i * 7000),
        isBuy: Math.random() > 0.5,
      }));
  }

  private setupRealtimeUpdates(): void {
    setInterval(() => {
      this.currentPrice += (Math.random() - 0.5) * (this.currentPrice * 0.005);
      this.priceChange = (Math.random() - 0.5) * 10;
      this.priceChangePercent = (this.priceChange / this.currentPrice) * 100;

      this.favorites.forEach((fav) => {
        fav.price += (Math.random() - 0.5) * (fav.price * 0.002);
        fav.change += (Math.random() - 0.5) * 0.1;
      });

      this.allAssets.forEach((asset) => {
        asset.price += (Math.random() - 0.5) * (asset.price * 0.002);
        asset.change += (Math.random() - 0.5) * 0.1;
      });
      this.recentTrades.unshift({
        price:
          this.currentPrice +
          (Math.random() - 0.5) * (this.currentPrice * 0.005),
        amount: parseFloat((Math.random() * 150 + 5).toFixed(2)),
        time: new Date(),
        isBuy: Math.random() > 0.5,
      });
      if (this.recentTrades.length > 30) {
        this.recentTrades.pop();
      }

      this.filterAssets();
    }, 2000);

    setInterval(() => {
      this.redrawAllDrawings();
    }, 5000);

    this.chart.timeScale().subscribeVisibleTimeRangeChange(() => {
      this.redrawAllDrawings();
    });
  }

  changeTimeframe(tf: string): void {
    this.selectedTimeframe = tf;
    this.loadChartData();
  }

  toggleIndicator(indicator: any): void {
    indicator.enabled = !indicator.enabled;

    switch (indicator.name) {
      case "EMA 20":
        if (indicator.enabled) {
          this.ema20Series = this.chart.addLineSeries({
            color: "#2962FF",
            lineWidth: 2,
            title: "EMA 20",
          });
          this.ema20Series.setData(this.currentEma20Data);
        } else if (this.ema20Series) {
          this.chart.removeSeries(this.ema20Series);
          this.ema20Series = null;
        }
        break;

      case "EMA 50":
        if (indicator.enabled) {
          this.ema50Series = this.chart.addLineSeries({
            color: "#FF6D00",
            lineWidth: 2,
            title: "EMA 50",
          });
          this.ema50Series.setData(this.currentEma50Data);
        } else if (this.ema50Series) {
          this.chart.removeSeries(this.ema50Series);
          this.ema50Series = null;
        }
        break;

      case "EMA 200":
        if (indicator.enabled) {
          this.ema200Series = this.chart.addLineSeries({
            color: "#E91E63",
            lineWidth: 2,
            title: "EMA 200",
          });
          this.ema200Series.setData(this.currentEma200Data);
        } else if (this.ema200Series) {
          this.chart.removeSeries(this.ema200Series);
          this.ema200Series = null;
        }
        break;

      case "Bollinger Bands":
        if (indicator.enabled) {
          this.bbUpperSeries = this.chart.addLineSeries({
            color: "rgba(156, 39, 176, 0.5)",
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            title: "BB Upper",
          });
          this.bbLowerSeries = this.chart.addLineSeries({
            color: "rgba(156, 39, 176, 0.5)",
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            title: "BB Lower",
          });
          this.bbUpperSeries.setData(this.currentBBData.upper);
          this.bbLowerSeries.setData(this.currentBBData.lower);
        } else if (this.bbUpperSeries && this.bbLowerSeries) {
          this.chart.removeSeries(this.bbUpperSeries);
          this.chart.removeSeries(this.bbLowerSeries);
          this.bbUpperSeries = null;
          this.bbLowerSeries = null;
        }
        break;

      case "Volume":
        if (indicator.enabled) {
          this.volumeSeries = this.chart.addHistogramSeries({
            color: "#26a69a",
            priceFormat: { type: "volume" },
            priceScaleId: "volume",
          });
          this.chart
            .priceScale("volume")
            .applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
          this.volumeSeries.setData(this.currentVolumeData);
        } else if (this.volumeSeries) {
          this.chart.removeSeries(this.volumeSeries);
          this.volumeSeries = null;
        }
        break;
    }
  }

  private initDrawingCanvas(): void {
    if (!this.drawingCanvas) return;
    const canvas = this.drawingCanvas.nativeElement;
    const container = this.chartContainer.nativeElement;
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight || 500;
    this.ctx = canvas.getContext("2d");
    this.ctx.lineCap = "round";
    this.ctx.lineJoin = "round";
  }

  private resizeDrawingCanvas(): void {
    if (!this.drawingCanvas) return;
    const canvas = this.drawingCanvas.nativeElement;
    const container = this.chartContainer.nativeElement;
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight || 500;
    this.redrawAllDrawings();
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.resizeDrawingCanvas();
  }

  onCanvasMouseDown(event: MouseEvent): void {
    if (!this.selectedDrawingTool) return;
    const rect = this.drawingCanvas.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const timeScale = this.chart.timeScale();
    const timeValue = timeScale.coordinateToTime(x);
    if (timeValue === null) return;
    const time = timeValue as number;
    const canvasHeight = rect.height;
    const visibleRange = this.getVisiblePriceRange();
    const price =
      visibleRange.max -
      (y / canvasHeight) * (visibleRange.max - visibleRange.min);
    this.isDrawing = true;
    this.drawingStartPoint = { x, y, time, price };
  }

  onCanvasMouseMove(event: MouseEvent): void {
    if (!this.isDrawing || !this.drawingStartPoint || !this.selectedDrawingTool)
      return;
    const rect = this.drawingCanvas.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    this.redrawAllDrawings();
    this.drawPreview(this.drawingStartPoint.x, this.drawingStartPoint.y, x, y);
  }

  onCanvasMouseUp(event: MouseEvent): void {
    if (!this.isDrawing || !this.drawingStartPoint || !this.selectedDrawingTool)
      return;
    const rect = this.drawingCanvas.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const timeScale = this.chart.timeScale();
    const timeValue = timeScale.coordinateToTime(x);
    if (timeValue === null) return;
    const time = timeValue as number;
    const canvasHeight = rect.height;
    const visibleRange = this.getVisiblePriceRange();
    const price =
      visibleRange.max -
      (y / canvasHeight) * (visibleRange.max - visibleRange.min);
    const drawing: Drawing = {
      id: Date.now().toString(),
      type: this.selectedDrawingTool,
      points: [
        {
          time: this.drawingStartPoint.time,
          price: this.drawingStartPoint.price,
        },
        { time, price },
      ],
      color: this.drawingColor,
    };
    this.drawings.push(drawing);
    this.isDrawing = false;
    this.drawingStartPoint = null;
    this.redrawAllDrawings();
  }

  private drawPreview(x1: number, y1: number, x2: number, y2: number): void {
    if (!this.ctx) return;
    this.ctx.strokeStyle = this.drawingColor;
    this.ctx.lineWidth = 2;
    this.ctx.setLineDash([]);
    switch (this.selectedDrawingTool) {
      case "trendline":
        this.ctx.beginPath();
        this.ctx.moveTo(x1, y1);
        this.ctx.lineTo(x2, y2);
        this.ctx.stroke();
        break;
      case "horizontal":
        this.ctx.beginPath();
        this.ctx.moveTo(0, y1);
        this.ctx.lineTo(this.drawingCanvas.nativeElement.width, y1);
        this.ctx.stroke();
        break;
      case "vertical":
        this.ctx.beginPath();
        this.ctx.moveTo(x1, 0);
        this.ctx.lineTo(x1, this.drawingCanvas.nativeElement.height);
        this.ctx.stroke();
        break;
      case "rectangle":
        this.ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
        this.ctx.fillStyle = this.drawingColor + "20";
        this.ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
        break;
      case "fibonacci":
        this.drawFibonacci(x1, y1, x2, y2);
        break;
      case "arrow":
        this.drawArrow(x1, y1, x2, y2);
        break;
    }
  }

  private redrawAllDrawings(): void {
    if (!this.ctx || !this.drawingCanvas) return;
    this.ctx.clearRect(
      0,
      0,
      this.drawingCanvas.nativeElement.width,
      this.drawingCanvas.nativeElement.height,
    );
    const timeScale = this.chart.timeScale();
    const visibleRange = this.getVisiblePriceRange();
    const canvasHeight = this.drawingCanvas.nativeElement.height;

    this.drawings.forEach((drawing) => {
      if (drawing.points.length < 2) return;
      const x1Coord = timeScale.timeToCoordinate(drawing.points[0].time as any);
      const x2Coord = timeScale.timeToCoordinate(drawing.points[1].time as any);
      if (x1Coord === null || x2Coord === null) return;
      const x1 = x1Coord as number;
      const x2 = x2Coord as number;
      const y1 =
        ((visibleRange.max - drawing.points[0].price) /
          (visibleRange.max - visibleRange.min)) *
        canvasHeight;
      const y2 =
        ((visibleRange.max - drawing.points[1].price) /
          (visibleRange.max - visibleRange.min)) *
        canvasHeight;

      this.ctx.strokeStyle = drawing.color;
      this.ctx.lineWidth = 2;
      this.ctx.setLineDash([]);

      switch (drawing.type) {
        case "trendline":
          this.ctx.beginPath();
          this.ctx.moveTo(x1, y1);
          this.ctx.lineTo(x2, y2);
          this.ctx.stroke();
          break;
        case "horizontal":
          this.ctx.beginPath();
          this.ctx.moveTo(0, y1);
          this.ctx.lineTo(this.drawingCanvas.nativeElement.width, y1);
          this.ctx.stroke();
          break;
        case "vertical":
          this.ctx.beginPath();
          this.ctx.moveTo(x1, 0);
          this.ctx.lineTo(x1, this.drawingCanvas.nativeElement.height);
          this.ctx.stroke();
          break;
        case "rectangle":
          this.ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
          this.ctx.fillStyle = drawing.color + "20";
          this.ctx.fillRect(x1, y1, x2 - x1, y2 - y1);
          break;
        case "fibonacci":
          this.drawFibonacci(x1, y1, x2, y2);
          break;
        case "arrow":
          this.drawArrow(x1, y1, x2, y2);
          break;
      }
    });
  }

  private getVisiblePriceRange(): { min: number; max: number } {
    if (!this.currentCandleData || this.currentCandleData.length === 0)
      return { min: 0, max: 100 };
    const timeScale = this.chart.timeScale();
    const visibleLogicalRange = timeScale.getVisibleLogicalRange();
    if (!visibleLogicalRange) {
      const prices: number[] = [];
      this.currentCandleData.forEach((c) => prices.push(c.high, c.low));
      return { min: Math.min(...prices), max: Math.max(...prices) };
    }
    const from = Math.max(0, Math.floor(visibleLogicalRange.from));
    const to = Math.min(
      this.currentCandleData.length - 1,
      Math.ceil(visibleLogicalRange.to),
    );
    const visibleData = this.currentCandleData.slice(from, to + 1);
    const prices: number[] = [];
    visibleData.forEach((c) => prices.push(c.high, c.low));
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }

  private drawFibonacci(x1: number, y1: number, x2: number, y2: number): void {
    const levels = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
    const height = y2 - y1;
    this.ctx.font = "11px -apple-system, sans-serif";
    this.ctx.fillStyle = this.drawingColor;
    levels.forEach((level) => {
      const y = y1 + height * level;
      this.ctx.setLineDash([5, 5]);
      this.ctx.beginPath();
      this.ctx.moveTo(x1, y);
      this.ctx.lineTo(x2, y);
      this.ctx.stroke();
      this.ctx.fillText(`${(level * 100).toFixed(1)}%`, x2 + 5, y + 4);
    });
  }

  private drawArrow(x1: number, y1: number, x2: number, y2: number): void {
    const headLength = 15;
    const angle = Math.atan2(y2 - y1, x2 - x1);
    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();
    this.ctx.beginPath();
    this.ctx.moveTo(x2, y2);
    this.ctx.lineTo(
      x2 - headLength * Math.cos(angle - Math.PI / 6),
      y2 - headLength * Math.sin(angle - Math.PI / 6),
    );
    this.ctx.moveTo(x2, y2);
    this.ctx.lineTo(
      x2 - headLength * Math.cos(angle + Math.PI / 6),
      y2 - headLength * Math.sin(angle + Math.PI / 6),
    );
    this.ctx.stroke();
  }

  selectDrawingTool(toolId: string): void {
    if (this.selectedDrawingTool === toolId) {
      this.selectedDrawingTool = null;
      this.drawingTools.forEach((t) => (t.active = false));
      this.drawingCanvas.nativeElement.style.pointerEvents = "none";
    } else {
      this.selectedDrawingTool = toolId;
      this.drawingTools.forEach((t) => (t.active = t.id === toolId));
      this.drawingCanvas.nativeElement.style.pointerEvents = "auto";
    }
  }

  clearAllDrawings(): void {
    this.drawings = [];
    if (this.ctx)
      this.ctx.clearRect(
        0,
        0,
        this.drawingCanvas.nativeElement.width,
        this.drawingCanvas.nativeElement.height,
      );
  }

  deleteLastDrawing(): void {
    this.drawings.pop();
    this.redrawAllDrawings();
  }

  resetChart(): void {
    if (this.chart && this.chart.timeScale) {
      this.chart.timeScale().fitContent();
    }
  }

  onAmountChange(amount: number): void {
    this.tradeAmount = amount;
    this.tradeTotal = this.tradePrice * this.tradeAmount;
  }

  onPriceChange(price: number): void {
    this.tradePrice = price;
    this.tradeTotal = this.tradePrice * this.tradeAmount;
  }

  onSliderChange(percent: number): void {
    this.sliderPercent = percent;
    const availableBalance = 10000;
    this.tradeAmount = (availableBalance * percent) / 100 / this.tradePrice;
    this.tradeTotal = this.tradePrice * this.tradeAmount;
  }

  buyAsset(): void {
    alert(
      `Buy ${this.tradeAmount.toFixed(2)} ${this.selectedSymbol} at $${this.tradePrice.toFixed(2)}`,
    );
  }

  sellAsset(): void {
    alert(
      `Sell ${this.tradeAmount.toFixed(2)} ${this.selectedSymbol} at $${this.tradePrice.toFixed(2)}`,
    );
  }

  selectAsset(asset: any): void {
    this.selectedSymbol = asset.symbol;
    this.selectedAssetName = asset.name;
    this.currentPrice = asset.price;
    this.priceChange = asset.price * (asset.change / 100);
    this.priceChangePercent = asset.change;
    this.tradePrice = asset.price;
    this.loadChartData();
  }

  filterAssets(): void {
    if (!this.searchQuery.trim()) {
      this.filteredAssets = [...this.allAssets];
    } else {
      const query = this.searchQuery.toLowerCase();
      this.filteredAssets = this.allAssets.filter(
        (asset) =>
          asset.symbol.toLowerCase().includes(query) ||
          asset.name.toLowerCase().includes(query),
      );
    }
  }

  hideSearchResults(): void {
    setTimeout(() => {
      this.showSearchResults = false;
      this.searchQuery = "";
    }, 200);
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-trader/insa-trader.component.ts